import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { UI_LANGUAGES } from '../constants';
import { normalizeRoutePath, splitLocalizedPath } from '../utils/routes';

export const LanguageSync = () => {
    const { activeLang, handleLangChange, uiLanguages } = useAppContext();
    const navigate = useNavigate();
    const location = useLocation();

    const rawPath = normalizeRoutePath(location.pathname);
    const { lang, normalizedPath } = splitLocalizedPath(location.pathname);

    useEffect(() => {
        if (normalizedPath !== rawPath) {
            void navigate(`${normalizedPath}${location.search}${location.hash}`, { replace: true });
            return;
        }

        if (!lang) {
            if (location.pathname === '/') {
                void navigate(`/${activeLang.code.toLowerCase()}`, { replace: true });
            }
            return;
        }

        const code = lang.toUpperCase();
        const targetLang = uiLanguages.find(l => l.code === code) ||
            UI_LANGUAGES.find(l => l.code === code);

        if (targetLang) {
            document.documentElement.lang = lang;
            if (activeLang.code !== targetLang.code) {
                handleLangChange(targetLang);
            }
        } else {
            void navigate(`/lt${location.pathname.substring(3)}`, { replace: true });
        }
    }, [activeLang.code, handleLangChange, lang, location.hash, location.pathname, location.search, navigate, normalizedPath, rawPath, uiLanguages]);

    // Block children from rendering until context lang matches the URL lang.
    // This prevents a one-render flash where DB content is in the new lang
    // but AppContext UI strings (t!.nav, t!.services, etc.) are still in the old lang.
    // On initial page load getInitialLang() already reads the URL so this is never
    // triggered; it only fires during an in-page language switch.
    const urlLangCode = lang?.toUpperCase();
    if (normalizedPath !== rawPath || (urlLangCode && activeLang.code !== urlLangCode)) {
        return null;
    }

    return <Outlet />;
};
